/**
 * caseBack.js — PROD-654 (patient P2): the header back button inside a case returns to the
 * PREVIOUS SCREEN of the case flow.
 *
 * ★★★ THE DEFECT (Alex 2026-10-06, measured on a0a93405). Twelve header back handlers hard-coded
 *   onBack={() => navigate('/case/home')} (Deliver x2, Describe voice, Detect, Diagnose x3, Share
 *   choice, Share x4), so back from Detect, Diagnose, the doctor list or the share code always
 *   jumped to the case home instead of the screen the patient came from. And the forward steps of
 *   the flow navigated with { replace: true }, so the browser / iOS swipe-back skipped the
 *   replaced screen too.
 *
 * ★★ THE RULE (released by Alex 2026-10-06 ~17:14 PDT): history when the previous entry is inside
 *   the case flow (a /case/... path), else a defined parent; the case home only on direct entry
 *   (e.g. from a notification, a reload) and after doctor-sent.
 *
 * HOW "THE PREVIOUS ENTRY" IS KNOWN. React Router keeps its history index in
 *   window.history.state.idx. App records the pathname of every entry it renders under that index
 *   (recordCaseTrail); the entry below the current one is trail[idx - 1]. Nothing is persisted: after
 *   a reload the trail is empty, which reads as a direct entry (the parent), the safe answer.
 *   `skip` names entries that would bounce straight forward (Share choice once the case has picked
 *   its side): back steps over them, at most three entries deep.
 */
export const CASE_HOME = '/case/home'

const trail = new Map() // history index -> pathname

export const isCaseFlowPath = (p) => typeof p === 'string' && (p === '/case' || p.startsWith('/case/'))

export function historyIndex(state) {
  const s = state !== undefined ? state : (typeof window !== 'undefined' ? window.history.state : null)
  const i = s && s.idx
  return Number.isInteger(i) ? i : null
}

/** App calls this for every location it renders. */
export function recordCaseTrail(pathname, state) {
  const i = historyIndex(state)
  if (i !== null && typeof pathname === 'string') trail.set(i, pathname)
}

/** The pathname of the entry below the current one, or null when it is not known (direct entry). */
export function previousPath(state) {
  const i = historyIndex(state)
  if (i === null || i < 1) return null
  return trail.has(i - 1) ? trail.get(i - 1) : null
}

/**
 * Back from a case screen.
 * @returns {{how: 'history', delta: number, to: string} | {how: 'parent', to: string}}
 */
export function caseBack(navigate, { parent = CASE_HOME, skip = () => false, state } = {}) {
  const i = historyIndex(state)
  if (i !== null) {
    for (let d = 1; d <= 3 && i - d >= 0; d++) {
      const p = trail.get(i - d)
      if (!isCaseFlowPath(p)) break
      if (skip(p)) continue
      navigate(-d)
      return { how: 'history', delta: -d, to: p }
    }
  }
  navigate(parent, { replace: true })
  return { how: 'parent', to: parent }
}

/** For tests only: forget the trail. */
export function __resetCaseTrail() { trail.clear() }
